import { REDO_ONBOARDING_APP_URL } from "@redotech/redo-merchant-app-common/config";
import { TcAcceptResponse } from "@redotech/redo-model/onboarding";

/**
 * Decides whether the merchant app should navigate to the post-T&C redirect
 * URL returned by the server, or hide the modal and stay on the merchant
 * app. Pure function — no side effects.
 *
 * Returns the URL to navigate to, or null to suppress the redirect.
 *
 * Validation rules:
 *  - response.redirectUrl must be a non-empty string
 *  - it must start with the known onboarding-app origin (allowlist; prevents
 *    open-redirect-shaped behavior if the server response is ever malformed)
 *  - it must contain a token query param (defensive — auth handoff requires
 *    it; without it the merchant lands unauthenticated)
 *  - Shopify test stores stay on the merchant app regardless (matches the
 *    pre-existing behavior in onboarding/index.tsx for those teams)
 */
export function shouldRedirect(args: {
  response: TcAcceptResponse | undefined;
  isShopifyTestStore: boolean;
}): string | null {
  if (args.isShopifyTestStore) {
    return null;
  }
  const url = args.response?.redirectUrl;
  if (typeof url !== "string" || url === "") {
    return null;
  }
  // Origin equality (not startsWith) — prevents bypass via subdomain-suffix
  // attacks like https://setup.getredo.com.evil.com/... that would otherwise
  // pass a naive prefix check.
  let parsed: URL;
  let allowed: URL;
  try {
    parsed = new URL(url);
    allowed = new URL(REDO_ONBOARDING_APP_URL);
  } catch {
    return null;
  }
  if (parsed.origin !== allowed.origin) {
    return null;
  }
  // The onboarding app's initAuthFromUrl() reads ?token=...; without it the
  // merchant lands on a login wall. Treat a missing token as a server bug
  // and suppress the redirect rather than send the user to an auth dead-end.
  const token = parsed.searchParams.get("token");
  if (!token) {
    return null;
  }
  return url;
}
